import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import {
  getByOrganizationId as getAuthenticationConfigurationByIdFn,
  GetByOrganizationId$Params,
} from '../../api/fn/authentication-configuration/get-by-organization-id';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  AuthenticationConfiguration,
  CreateAuthenticationConfigurationRequest,
  UpdateAuthenticationConfigurationRequest,
} from '../../api/models';
import { createAuthenticationConfiguration as createAuthenticationConfigurationFn } from '../../api/fn/authentication-configuration/create-authentication-configuration';
import {
  AuthTypeValues,
  CreateAuthenticationConfigurationParams,
} from '../../types/types';
import { updateAuthenticationConfiguration as updateAuthenticationConfigurationFn } from '../../api/fn/authentication-configuration/update-authentication-configuration';
import {
  deleteAuthenticationConfiguration as deleteAuthenticationConfigurationFn,
  DeleteAuthenticationConfiguration$Params,
} from '../../api/fn/authentication-configuration/delete-authentication-configuration';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  constructor(private apiService: ApiService) {}

  getAuthenticationConfig(
    params: GetByOrganizationId$Params
  ): Observable<AuthenticationConfiguration | undefined> {
    return this.apiService
      .invoke(getAuthenticationConfigurationByIdFn, params)
      .pipe(
        map((data) => data.authenticationConfiguration),
        catchError((e) => {
          return throwError(() => e);
        })
      );
  }

  createAuthenticationConfig(
    params: CreateAuthenticationConfigurationParams
  ): Observable<boolean> {
    const { organizationId, authType, authConfigValues, secretName } = params;
    const authConfig: AuthenticationConfiguration = {};

    if (authType === AuthTypeValues.OAUTH2) {
      authConfig.oAuth2Configuration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
      };
    } else if (authType === AuthTypeValues.ENTRAID) {
      authConfig.entraIdConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        tenantId: authConfigValues.tenantId!,
      };
    } else if (authType === AuthTypeValues.OPENIDCONNECT) {
      authConfig.openIdConnectConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
      };
    }

    const authConfigBody: CreateAuthenticationConfigurationRequest = {
      configuration: authConfig,
    };

    return this.apiService
      .invoke(createAuthenticationConfigurationFn, {
        organizationId: organizationId,
        body: authConfigBody,
      })
      .pipe(
        map(() => true),
        catchError((e) => {
          return of(false);
        })
      );
  }

  updateAuthenticationConfig(
    params: CreateAuthenticationConfigurationParams
  ): Observable<boolean> {
    const { organizationId, authType, authConfigValues, secretName } = params;
    const authConfig: AuthenticationConfiguration = {};

    if (authType === AuthTypeValues.OAUTH2) {
      authConfig.oAuth2Configuration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
      };
    } else if (authType === AuthTypeValues.ENTRAID) {
      authConfig.entraIdConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        tenantId: authConfigValues.tenantId!,
      };
    } else if (authType === AuthTypeValues.OPENIDCONNECT) {
      authConfig.openIdConnectConfiguration = {
        authorizationEndpoint: authConfigValues.auth_endpoint,
        tokenEndpoint: authConfigValues.token_endpoint,
        logoutEndpoint: authConfigValues.logout_endpoint ?? '',
        clientId: authConfigValues.clientId,
        clientSecretReference: secretName,
        grantType: authConfigValues.grant_type,
        scope: authConfigValues.scope,
        selfDiscoveryEndpoint: authConfigValues.self_discovery_endpoint,
        externalUserIdClaimName: authConfigValues.ext_userId_claim,
      };
    }

    const authConfigBody: UpdateAuthenticationConfigurationRequest = {
      configuration: authConfig,
    };

    return this.apiService
      .invoke(updateAuthenticationConfigurationFn, {
        organizationId: organizationId,
        body: authConfigBody,
      })
      .pipe(
        map(() => true),
        catchError((e) => {
          return of(false);
        })
      );
  }

  deleteAuthenticationConfig(
    params: DeleteAuthenticationConfiguration$Params
  ): Observable<boolean> {
    return this.apiService
      .invoke(deleteAuthenticationConfigurationFn, params)
      .pipe(
        map(() => true),
        catchError((e) => {
          return of(false);
        })
      );
  }
}
